<template>
  <kd-layout-fixed-bottom-button class="delivery-address-edit-page" button-text="确定" @click="save">
    <img v-if="displayBannerUrl" class="banner" :src="displayBannerUrl" alt="" />

    <div class="form-wrapper">
      <h2 class="title">收货信息</h2>

      <div class="caption" v-if="caption">{{ caption }}</div>

      <form class="form" method="dialog" onclick="return false">
        <div class="form-item">
          <label class="label" for="name">收件人：</label>
          <kd-input v-model.trim="form.name" id="name" name="name" maxlength="10" placeholder="请输入姓名" />
        </div>

        <div class="form-item">
          <label class="label" for="phone">手机号码：</label>
          <kd-input
            v-model.trim="form.phone"
            id="phone"
            name="phone"
            maxlength="11"
            inputmode="tel"
            placeholder="请输入手机号码"
          />
        </div>

        <div class="form-item">
          <label class="label" for="region">所在地区：</label>
          <kd-input
            id="region"
            name="region"
            :value="regionStr"
            placeholder="请选择"
            readonly
            autocomplete="off"
            @click="regionPickerParams.visible = true"
          >
            <template #append>
              <kd-icon icon="arrow-down" />
            </template>
          </kd-input>
          <kd-region-picker :value="region" :visible.sync="regionPickerParams.visible" @select="region = $event" />
        </div>

        <div class="form-item">
          <label class="label" for="address">详细地址：</label>
          <kd-input v-model.trim="form.address" id="address" name="address" placeholder="如街道、门牌号、小区、单元" />
        </div>
      </form>

      <p class="tips title" v-if="showTips">温馨提示：</p>
      <p class="tips" v-if="showTips">1. 收货人信息默认使用您在天天跳绳的认证过程中所提供的身份信息。</p>
    </div>

    <GiftSkuSelectBottomPopup
      v-if="lotterySkus.length > 1"
      :visible.sync="skuPickerVisible"
      :skus="lotterySkus"
      :prize-group-name="prizeGroupName || ''"
      @confirm="onSkuConfirm"
    />
  </kd-layout-fixed-bottom-button>
</template>

<script lang="ts">
import { UIUtil } from 'kuaidong-lib'
import { consumeActivityLotteryAddressSkus } from 'kuaidong-activity/src/service/giftAddressService'
import { ADeliveryAddress, AGiftOrder, ASchoolAddress } from 'kuaidong-api'
import { BasePage, Component, Prop } from 'kuaidong-component'
import { DeliveryAddress, Region, ITeacherAddressInfo, ITeacherAddressInfoForm } from 'kuaidong-model'
import { KdLayoutFixedBottomButton, KdInput, KdRegionPicker } from 'kuaidong-ui'

import GiftSkuSelectBottomPopup, { IGiftSkuOption } from './GiftSkuSelectBottomPopup.vue'

@Component({
  components: {
    GiftSkuSelectBottomPopup,
    KdInput,
    KdRegionPicker,
    KdLayoutFixedBottomButton,
  },
})
export default class DeliveryAddressEditPage extends BasePage {
  @Prop({ required: false })
  type?: 'school' | ''

  @Prop({ required: false })
  addressId?: string

  @Prop({ required: false })
  bannerUrl?: string // 显示bannner

  @Prop({ required: false })
  caption?: string // 说明文字 eg： *体智云将在活动结束后发放满足条件教师的奖励

  @Prop({ required: false })
  nextPath?: string // 完成之后跳转地址

  @Prop({ required: false })
  nextPathQuery?: any // 完成之后跳转参数

  @Prop({ required: false })
  lotteryActivityId?: string

  @Prop({ required: false })
  prizeGroupName?: string

  currentAddress: ITeacherAddressInfo | null = null

  showTips = false

  displayBannerUrl = ''

  lotterySkus: IGiftSkuOption[] = []

  selectedSkuId = ''

  skuPickerVisible = false

  form = {
    address_id: '',
    name: '',
    phone: '',
    province: '',
    city: '',
    district: '',
    address: '',
    default: true,
  }

  regionPickerParams = {
    visible: false,
  }

  get region() {
    return {
      province: this.form.province,
      city: this.form.city,
      district: this.form.district,
    }
  }

  set region(region: Region) {
    this.form.province = region.province
    this.form.city = region.city
    this.form.district = region.district
  }

  get regionStr() {
    return [this.form.province, this.form.city, this.form.district].join(' ').trim()
  }

  get isSchoolAddress() {
    return this.type === 'school'
  }

  get isLotteryReceiveMode() {
    return this.lotterySkus.length > 0
  }

  async created() {
    this.displayBannerUrl = this.bannerUrl || ''
    this.initLotterySkusFromStorage()

    if (this.isSchoolAddress) {
      this.showTips = true
      const address = await ASchoolAddress.getPhysicalConfirmSchoolAddress()
      this.form = {
        ...this.form,
        ...(address || {}),
      }
      return
    }
    if (this.addressId) {
      // 传入id，进行编辑
      const addresses = await AGiftOrder.teacherAddressList()
      const currentAddress = addresses.find((x) => x.address_id === this.addressId)
      if (currentAddress) {
        this.currentAddress = currentAddress
        this.form = {
          ...currentAddress,
        }
      }
    } else {
      this.showTips = true
      const defaultAddress = await ADeliveryAddress.find() // 如果没有地址通过这个接口获得默认地址
      this.form = {
        address_id: '',
        ...defaultAddress,
        default: true,
      }
    }
    this.form.default = true // 活动确认地址均设为默认
  }

  initLotterySkusFromStorage() {
    if (!this.lotteryActivityId) {
      return
    }
    const _skus = consumeActivityLotteryAddressSkus<IGiftSkuOption>(this.lotteryActivityId)
    if (!_skus?.length) {
      return
    }
    this.lotterySkus = _skus

    if (this.lotterySkus.length > 1) {
      this.$nextTick(() => {
        this.skuPickerVisible = true
      })
    } else if (this.lotterySkus.length === 1) {
      this.selectedSkuId = this.lotterySkus[0].id
      this.applyBannerBySkuId(this.selectedSkuId)
    }
  }

  applyBannerBySkuId(_id: string) {
    const _sku = this.lotterySkus.find((_s) => _s.id === _id)
    if (_sku) {
      this.displayBannerUrl = _sku.banner_img || _sku.img_url || this.displayBannerUrl
    }
  }

  onSkuConfirm(_id: string) {
    this.selectedSkuId = _id
    this.applyBannerBySkuId(_id)
  }

  validate() {
    if (this.isLotteryReceiveMode && !this.selectedSkuId) {
      return UIUtil.checkFailed('请选择礼品规格')
    }

    if (!this.form.name) {
      return UIUtil.checkFailed('请填写收件人')
    }

    if (!this.form.phone) {
      return UIUtil.checkFailed('请填写手机号')
    }

    if (!this.form.province) {
      return UIUtil.checkFailed('请选择地区')
    }

    if (!this.form.address) {
      return UIUtil.checkFailed('请填写详细地址')
    }

    return true
  }

  async save() {
    if (this.validate()) {
      if (this.isSchoolAddress) {
        try {
          await ASchoolAddress.updatePhysicalConfirmSchoolAddress(this.form)
          this._jump2Activity()
        } catch (e) {
          this.toast('更新地址失败~')
        }
      } else if (this.form.address_id) {
        try {
          await AGiftOrder.updateTeacherAddress(this.form as ITeacherAddressInfo)
          this._jump2Activity()
        } catch (e) {
          this.toast('更新地址失败~')
        }
      } else {
        try {
          await AGiftOrder.addTeacherAddress(this.form as ITeacherAddressInfoForm)
          this._jump2Activity()
        } catch (e) {
          this.toast(e.data)
        }
      }
    }
  }

  // 如果传入参数，跳转进入活动页面
  _jump2Activity() {
    if (this.nextPath) {
      const _query = { ...(this.nextPathQuery || {}) }
      if (this.selectedSkuId) {
        _query.lottery = this.selectedSkuId
      }
      this.$router.replace({
        path: this.nextPath,
        query: _query,
      })
    }
  }
}
</script>

<style lang="less">
.kd-layout-fixed-bottom-button.delivery-address-edit-page {
  > .kd-layout-fixed-bottom__main {
    padding: 4px 0 20px 0;
    background: #fff;

    > .banner {
      width: 100%;
      filter: drop-shadow(0 2px 12px rgba(2, 113, 255, 0.3));
      border-radius: 12px;
    }

    > .form-wrapper {
      margin: 20px 16px 0 16px;
      text-align: left;

      > .title {
        line-height: 24px;
        font-size: 16px;
        font-weight: 600;
        color: #2d2f33;
      }

      > .caption {
        margin: 4px 0 0 0;
        line-height: 17px;
        font-size: 12px;
        color: #8f8f8f;
      }

      > .form {
        > .form-item {
          margin: 16px 0 0 0;

          > .label {
            line-height: 20px;
            font-size: 14px;
            font-weight: 500;
            color: #8f8f8f;
          }

          > .kd-input {
            margin: 8px 0 0 0;
          }
        }
      }

      > .tips {
        margin: 16px 0 0 0;
        line-height: 18px;
        font-size: 12px;
        color: #8f8f8f;

        + .tips {
          margin: 8px 0 0 0;
        }
      }
    }
  }
}
</style>
